/**
 * C-Search → Compare. The same criteria against the new search and the legacy unified search, side by side — the
 * manual form of ADR-040 §C P2 (shadow compare), which has to read clean before any client moves (P3).
 *
 * The legacy side is pinned to `NUMEO_SEARCH`, the database read: a compare never calls a live loadboard, never spends
 * a DAT request, never meters a quota. Both sides return one page, newest first, so a full page on either side is
 * flagged — loads beyond it were not compared, and a "missing" load there may just be on page two.
 *
 * The server makes the two sides ask the same question: an empty "Posted within" becomes 24 h on both (legacy's own
 * default), and the picked sources filter the legacy results, since `NUMEO_SEARCH` spans every source.
 */

import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import {
    SEARCH_COMPARE_FIELDS,
    type LoadPipelineSearchCompareField,
    type LoadPipelineSearchCompareResponse,
    type LoadPipelineSearchCompareSide,
    type LoadPipelineSearchCompareSideSummary,
} from '@numeo/types-core'
import { loadPipelineApi } from 'api/loadPipeline'
import * as React from 'react'
import { ErrorState, Stat, StatRow, formatNumber } from './components/shared'
import { SearchCriteriaForm, defaultSearchForm, toCriteria } from './components/SearchCriteriaForm'
import { SEARCH_UNREACHABLE, age } from './SearchProbePage'

const FIELD_LABEL: Record<LoadPipelineSearchCompareField, string> = {
    rate: 'Rate',
    pickupDate: 'Pickup date',
    originState: 'Origin state',
    destinationState: 'Destination state',
    equipment: 'Equipment',
    active: 'Open / closed',
}

const SIDE_LABEL: Record<LoadPipelineSearchCompareSide, { label: string; color: 'success' | 'primary' | 'warning' }> = {
    BOTH: { label: 'Both', color: 'success' },
    NEW_ONLY: { label: 'New only', color: 'primary' },
    LEGACY_ONLY: { label: 'Legacy only', color: 'warning' },
}

const usd = (n: number | null): string => (n === null ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`)

function sideStat(label: string, side: LoadPipelineSearchCompareSideSummary) {
    return (
        <Stat
            label={label}
            value={side.error ? 'failed' : formatNumber(side.count)}
            hint={
                side.error
                    ? `${side.errorKind ? `[${side.errorKind}] ` : ''}${side.error}`
                    : `${side.latencyMs} ms${side.truncated ? ' · full page, more not compared' : ''}`
            }
            tone={side.error ? 'danger' : side.truncated ? 'warning' : 'neutral'}
        />
    )
}

export default function LoadPipelineSearchComparePage() {
    const [form, setForm] = React.useState(defaultSearchForm)
    const [data, setData] = React.useState<LoadPipelineSearchCompareResponse | null>(null)
    const [error, setError] = React.useState<unknown>(null)
    const [loading, setLoading] = React.useState(false)
    const [filter, setFilter] = React.useState<'ALL' | 'DIFFS' | LoadPipelineSearchCompareSide>('ALL')

    const run = async () => {
        const { criteria } = toCriteria(form)
        if (!criteria) return
        setLoading(true)
        setError(null)
        try {
            setData(await loadPipelineApi.searchCompare(criteria, form.projectName))
        } catch (err) {
            setError(err)
        } finally {
            setLoading(false)
        }
    }

    const total = data ? data.counts.BOTH + data.counts.NEW_ONLY + data.counts.LEGACY_ONLY : 0
    const overlap = total ? Math.round((data!.counts.BOTH / total) * 100) : null
    const disagreeing = data ? data.rows.filter((r) => r.diffs.length > 0).length : 0
    const rows = (data?.rows ?? []).filter((r) => (filter === 'ALL' ? true : filter === 'DIFFS' ? r.diffs.length > 0 : r.side === filter))

    return (
        <Box>
            <SearchCriteriaForm value={form} onChange={setForm} onSubmit={run} submitting={loading} submitLabel="Compare" showSort={false} />
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: -1, mb: 2 }}>
                Empty &quot;Posted within&quot; compares the last 24 h on both sides (legacy&apos;s default). Picked sources also filter the legacy results.
            </Typography>

            {error ? <ErrorState error={error} unreachable={SEARCH_UNREACHABLE} /> : null}

            {data ? (
                <>
                    <StatRow>
                        {sideStat('New search', data.newSearch)}
                        {sideStat('Legacy (NUMEO_SEARCH)', data.legacy)}
                        <Stat
                            label="Overlap"
                            value={overlap === null ? '—' : `${overlap}%`}
                            hint={`${formatNumber(data.counts.BOTH)} found by both, of ${formatNumber(total)}`}
                            tone={overlap === null ? 'neutral' : overlap >= 95 ? 'success' : overlap >= 80 ? 'warning' : 'danger'}
                        />
                        <Stat label="New only" value={formatNumber(data.counts.NEW_ONLY)} hint="in the new store, not legacy" />
                        <Stat label="Legacy only" value={formatNumber(data.counts.LEGACY_ONLY)} hint="in legacy, missing from the new store" />
                        <Stat
                            label="Disagree"
                            value={formatNumber(disagreeing)}
                            hint="found by both, a field differs"
                            tone={disagreeing > 0 ? 'warning' : 'neutral'}
                        />
                    </StatRow>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                        <Typography level="body-sm">Field disagreements:</Typography>
                        {SEARCH_COMPARE_FIELDS.map((f) => (
                            <Chip key={f} size="sm" variant="soft" color={data.fieldDiffs[f] > 0 ? 'warning' : 'neutral'}>
                                {FIELD_LABEL[f]} {formatNumber(data.fieldDiffs[f])}
                            </Chip>
                        ))}
                        <Box sx={{ flex: 1 }} />
                        <Select size="sm" value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ minWidth: 170 }}>
                            <Option value="ALL">All rows</Option>
                            <Option value="DIFFS">Disagreeing only</Option>
                            <Option value="NEW_ONLY">New only</Option>
                            <Option value="LEGACY_ONLY">Legacy only</Option>
                            <Option value="BOTH">Both</Option>
                        </Select>
                    </Box>

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 640 }}>
                        <Table size="sm" stickyHeader>
                            <thead>
                                <tr>
                                    <th>Side</th>
                                    <th>External id</th>
                                    <th>Source (new / legacy)</th>
                                    <th>Origin</th>
                                    <th>Destination</th>
                                    <th>Pickup</th>
                                    <th>Equip.</th>
                                    <th style={{ textAlign: 'right' }}>Rate new</th>
                                    <th style={{ textAlign: 'right' }}>Rate legacy</th>
                                    <th>Status (new)</th>
                                    <th style={{ textAlign: 'right' }}>Posted new</th>
                                    <th style={{ textAlign: 'right' }}>Posted legacy</th>
                                    <th>Differs on</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={`${r.side}|${r.externalId}`}>
                                        <td>
                                            <Chip size="sm" variant="soft" color={SIDE_LABEL[r.side].color}>
                                                {SIDE_LABEL[r.side].label}
                                            </Chip>
                                        </td>
                                        <td>
                                            <code>{r.externalId}</code>
                                        </td>
                                        <td>
                                            {r.newSource ?? '—'} / {r.legacySource ?? '—'}
                                        </td>
                                        <td>{r.origin ?? '—'}</td>
                                        <td>{r.destination ?? '—'}</td>
                                        <td>{r.pickupDate ?? '—'}</td>
                                        <td>{r.equipment ?? '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{usd(r.newRateUsd)}</td>
                                        <td style={{ textAlign: 'right' }}>{usd(r.legacyRateUsd)}</td>
                                        <td>{r.newStatus ?? '—'}</td>
                                        <td style={{ textAlign: 'right' }} title={r.firstSeenAt ?? undefined}>
                                            {r.firstSeenAt ? age(r.firstSeenAt) : '—'}
                                        </td>
                                        <td style={{ textAlign: 'right' }} title={r.legacyPostedAt ?? undefined}>
                                            {r.legacyPostedAt ? age(r.legacyPostedAt) : '—'}
                                        </td>
                                        <td>{r.diffs.length ? r.diffs.map((d) => FIELD_LABEL[d]).join(', ') : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {rows.length === 0 ? (
                            <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                                No rows for this filter.
                            </Typography>
                        ) : null}
                    </Sheet>
                </>
            ) : null}
        </Box>
    )
}
